import { FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';
import { AvatarList } from '@molecules/dataDisplay';
import { LoadingButton } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { AssigneeOld } from '@libs/models/shifts/shift';
import { useLocationStore } from '@libs/store/locations';
import { Box, Stack } from '@mui/material';

type AssigneeButton = {
  value: AssigneeOld[];
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  hideUnassign?: boolean;
  onAssignClick?: () => void;
  onUnassignClick?: () => void;
};
export function AssigneeButton({
  fullWidth,
  loading,
  onAssignClick,
  onUnassignClick,
  value,
  disabled,
  hideUnassign,
}: AssigneeButton) {
  const theme = useTheme();
  const { current } = useLocationStore();
  const openUnassign = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    onUnassignClick?.();
  };

  if (value.length === 0) {
    return (
      <>
        <LoadingButton
          id="assign"
          onClick={(event) => {
            event.stopPropagation();
            onAssignClick?.();
          }}
          loading={loading}
          rounded
          disabled={disabled}
          fullWidth={fullWidth}
        >
          <Fade in={!loading} timeout={150}>
            <Stack direction="row" alignItems="center">
              <Box fontSize={16} mr={1} ml={1}>
                <FaIcon
                  name="user-magnifying-glass"
                  color={disabled ? theme.palette.action.disabled : theme.palette.primary.main}
                />
              </Box>
              <FormattedMessage id="shifts.assignBtnLabel" />
            </Stack>
          </Fade>
        </LoadingButton>
      </>
    );
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" height="100%" width="100%">
        <Fade in={!loading} timeout={150}>
          <Stack
            direction="row"
            alignItems={"center"}
            gap={1.75}
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <AvatarList
              list={value.map((v) => ({
                id: v.id,
                avatar: v.imageUrl,
                name: v.name,
                isInternal: v.isInternal,
                isOriented: !!v.orientedList && v.orientedList.find((o) => o.locationId === current?.id)?.value,
              }))}
            />
          </Stack>
        </Fade>

        {!hideUnassign && <Tooltip
          title={disabled ? <FormattedMessage id="shifts.unassignButton.disabledTooltip" /> : ''}
          placement="left"
        >
          <span>
            <Fade in={!loading} timeout={150}>
              <IconButton sx={{ width: '40px' }} onClick={openUnassign} disabled={disabled}>
                <FaIcon name="xmark" variant="light" color="inherit" />
              </IconButton>
            </Fade>
          </span>
        </Tooltip>}
      </Stack>
    </>
  );
}
